export const TIER_ORDER = ["FREE", "STARTER", "PRO", "AGENCY", "ENTERPRISE"] as const;
export type TierKey = (typeof TIER_ORDER)[number];

export type FeatureRule =
  | { minTier: TierKey }
  | { access: "owner" }
  | { access: "tester" };

export const FEATURE_RULES = {
  slideshow_builder: { minTier: "FREE" },
  prompt_creator: { minTier: "FREE" },
  output_showcase: { minTier: "FREE" },
  content_library: { minTier: "STARTER" },
  publishing_hub: { minTier: "STARTER" },
  email_marketing: { minTier: "STARTER" },
  affiliate_portal: { minTier: "STARTER" },
  ebook_covers: { minTier: "STARTER" },
  rss_import: { minTier: "STARTER" },
  rss_auto_publish: { minTier: "PRO" },
  video_generation: { minTier: "PRO" },
  ai_agent: { minTier: "PRO" },
  marketing_manager: { minTier: "PRO" },
  automation_pipeline: { minTier: "PRO" },
  lead_generation: { minTier: "PRO" },
  campaigns: { minTier: "PRO" },
  social_scheduler: { minTier: "PRO" },
  live_support: { minTier: "PRO" },
  blog_rotation: { access: "owner" },
  blog_library: { access: "owner" },
  admin_inbox: { access: "owner" },
  admin_members: { access: "owner" },
  testimonial_admin: { access: "owner" },
  tester_questionnaire: { access: "tester" },
  tester_uploads: { access: "tester" },
  content_permission: { access: "tester" },
} as const satisfies Record<string, FeatureRule>;

export type FeatureKey = keyof typeof FEATURE_RULES;

export interface AccessContext {
  tier: string | null;
  role: string | null;
  isOwner: boolean;
}

export function getFeatureRule(feature: FeatureKey): FeatureRule {
  return FEATURE_RULES[feature];
}

export function tierAtLeast(tier: string | null | undefined, minTier: TierKey): boolean {
  if (!tier) return false;
  const idx = TIER_ORDER.indexOf(tier as TierKey);
  return idx >= 0 && idx >= TIER_ORDER.indexOf(minTier);
}

export function canUseFeature(feature: FeatureKey, ctx: AccessContext): boolean {
  if (ctx.isOwner) return true;
  const rule = FEATURE_RULES[feature];
  if ("minTier" in rule) return tierAtLeast(ctx.tier ?? "FREE", rule.minTier);
  if (rule.access === "tester") return ctx.role === "tester";
  return false;
}

export function requiredTierLabel(feature: FeatureKey): string | null {
  const rule = FEATURE_RULES[feature];
  if (!("minTier" in rule)) return null;
  return rule.minTier.charAt(0) + rule.minTier.slice(1).toLowerCase();
}
